import { css } from '@emotion/react';
import { Box, Flex, Text } from '@theme-ui/components';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import Checkmark from '@app/components/Checkmark';
import { pushPerpetualEcommerceEvent } from '@app/helpers/tracking';
import { useFireOnce } from '@app/hooks/useFireOnce';
import useLocaleWithFallback from '@app/hooks/useLocaleWithFallback';
import colors from '@app/styles/theme/colors';
import { rem } from '@app/styles/theme/layout';

import { getVariantLabel } from '@app/helpers/shop';
import type { PricingPage_content_pricing_pages_layout_item_pricing_pages_multi_layout_products_products_id as DirectusProduct } from '../../__generated__/PricingPage';
import { isJPHardware } from '../../helpers';
import type { PricingPageShopProduct_magento2_products_items_Magento2_ConfigurableProduct_variants as Variant } from '../../hook/__generated__/PricingPageShopProduct';
import useProductVariant from '../../hook/useProductVariant';
import useUpgradePaths from '../../hook/useUpgradePaths';
import AddToCartButton from '../AddToCartButton/AddToCartButton';
import LicenseSelector from '../LicenseSelector';
import messages from '../ProductLayout/ProductLayout.messages';
import ProductVariantInfo from '../ProductVariantInfo';
import MultiProductGrid from './MultiProductGrid';
import { Content, Picture, Title, Wrapper } from './styles';

interface ProductTileProps {
  directusProduct: DirectusProduct;
  variants: (Variant | null)[] | null;
  isAuthenticated?: boolean;
  isPrimary: boolean;
}

const ConfigurableProductTile = ({
  directusProduct,
  variants,
  isAuthenticated,
  isPrimary,
}: ProductTileProps) => {
  const { selectedVariant, setSelectedVariant } = useProductVariant(variants);
  const { findTranslationByKeyWithFallback } = useLocaleWithFallback();
  const { upgradeVariants } = useUpgradePaths({
    skip: !isAuthenticated,
  });
  const fireEventOnce = useFireOnce();
  const { title, translations, sku } = directusProduct;

  useEffect(() => {
    if (selectedVariant && isPrimary) {
      fireEventOnce(() => {
        pushPerpetualEcommerceEvent('view_item', {
          currency:
            selectedVariant.product?.priceRange.minimumPrice.finalPrice
              .currency,
          value:
            selectedVariant.product?.priceRange.minimumPrice.finalPrice.value,
          items: [
            {
              // The parent product SKU and name is expected for item_id and item_name.
              item_id: sku || selectedVariant.product?.sku || '',
              item_name: title || selectedVariant.product?.name || '',
              sku_id: selectedVariant?.product?.sku || '',
              sku_name: selectedVariant?.product?.name || '',
              sdbs_id: selectedVariant?.product?.sdbsId || '',
              item_version: getVariantLabel(selectedVariant) || '',
              item_variant: selectedVariant.product?.name || '',
              price:
                selectedVariant.product?.priceRange.minimumPrice.finalPrice
                  .value || 0,
              quantity: 1,
            },
          ],
        });
      });
    }
  }, [selectedVariant, isPrimary, fireEventOnce]);

  const content = findTranslationByKeyWithFallback({
    translations,
    key: 'multiproduct_layout_content',
  });

  return (
    <Wrapper>
      <Picture
        data-testid="configurable-product-tile-image"
        src={selectedVariant?.product?.image?.url ?? undefined}
        alt={selectedVariant?.product?.image?.label ?? undefined}
      />
      <Title data-testid="configurable-product-tile-title" as="h3">
        {title}
      </Title>
      {content && <Content dangerouslySetInnerHTML={{ __html: content }} />}
      <Flex mb={3}>
        <Box sx={{ flexGrow: 1 }}>
          {variants && (
            <LicenseSelector
              variants={variants}
              setSelectedVariant={setSelectedVariant}
              selectedVariant={selectedVariant}
              upgradeVariants={upgradeVariants}
              isAuthenticated={isAuthenticated}
            >
              {(props) => <MultiProductGrid {...props} />}
            </LicenseSelector>
          )}
        </Box>
      </Flex>
      <Flex
        sx={{
          flexGrow: 1,
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
      >
        {selectedVariant && (
          <ProductVariantInfo hidePrice variant={selectedVariant} />
        )}
      </Flex>
      <AddToCartButton product={selectedVariant?.product} />
      {isJPHardware(selectedVariant?.product) && (
        <Flex py={3}>
          <Checkmark
            color="white"
            css={css`
              display: flex;
              flex: 1;
              min-width: 24px;
              height: 24px;
              background-color: ${colors.acidGreen};
              border-radius: 99999px;
              margin-right: 12px;
              &:after {
                top: 3px;
                left: 7px;
              }
            `}
          />
          <Text
            sx={{ fontWeight: '500', fontSize: rem(12), lineHeight: '1.6' }}
          >
            <FormattedMessage {...messages.mediaIntegrtion} />
          </Text>
        </Flex>
      )}
    </Wrapper>
  );
};

export default ConfigurableProductTile;
